class FlickityPlayCustomElement extends HTMLElement {
  constructor() {
    super();

    this._autoplayTimer = null;
    this._autoplaying = false;

    // 页面可见性监听
    this._onVisibilityChange = () => {
      if (document.hidden) {
        this.stopCustomAutoplay();
      }else {
        this.startCustomAutoplay();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    } else {
      Motion.inView(this, this.init.bind(this), {
        margin: "200px 0px 200px 0px",
      });
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.items.length - 1).toString()
    );
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  get speed() {
    return this.autoplay
      ? parseInt(this.getAttribute("autoplay-speed") || "5") * 1000
      : 5000;
  }

  get wrapAround() {
    return !this.hasAttribute("wrap-around-false");
  }

  get paginationType() {
    return this.getAttribute("pagination-type") || "";
  }

  init() {
    const that = this;
    if (this.items.length === 0) return;

    if (this.items.length === 1) {
      this.onReady(this.items[0]);
      return;
    }

    this.slider = new Flickity(this, {
      accessibility: false,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: this.wrapAround,
      rightToLeft: theme.config.rtl,
      autoPlay: false,
      adaptiveHeightProperty: false,
      on: {
        ready: function () {
          const { selectedElement } = this;
          that.onReady(selectedElement);
        },
      },
    });

    this.slider.on("change", this.onChange.bind(this));
    // 新增：拖拽时停止自动轮播
    this.slider.on("dragStart", () => {
      this.stopCustomAutoplay();
    });


    this.addEventListener("slider:previous", () => {
      this.stopCustomAutoplay();
      this.slider.previous();
    });

    this.addEventListener("slider:next", () => {
      this.stopCustomAutoplay();
      this.slider.next();
    });

    this.addEventListener("slider:play", () => this.startCustomAutoplay());
    this.addEventListener("slider:pause", () => this.stopCustomAutoplay());

    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => {
        const index = this.items.indexOf(event.target);
        if (index >= 0) this.slider.select(index);
      });
    }

    if (this.autoplay) {
      this.startCustomAutoplay();
    }
  }

  startCustomAutoplay() {
    this.stopCustomAutoplay("forclear");
    this._autoplaying = true;
    this.scheduleNextSlide();
  }

  stopCustomAutoplay(why) {
    this._autoplaying = false;
    if (this._autoplayTimer) {
      clearTimeout(this._autoplayTimer);
      this._autoplayTimer = null;
    }
    if (why !== "forclear") {
      let parent = this.dataset.sliderId;
      this.dispatchEvent(new CustomEvent("slider:autoplay-stop", { bubbles: true, detail: { sectionId: parent } }));
    } else {
      this.dispatchEvent(new CustomEvent("slider:autoplay-start", { bubbles: true }));
    }
  }


  scheduleNextSlide() {
    if (!this._autoplaying) return;

    const currentIndex = this.slider.selectedIndex;
    const currentSlide = this.items[currentIndex];
    const video = currentSlide.querySelector("video");
    const schedule = (duration) => {
      if (video) {
        let blockId = currentSlide.getAttribute('data-block-id');
        if (blockId) {
          const dot = document.querySelector(`.flickity-page-dot-${blockId}`);
          if (dot) {
            dot.style.setProperty('width', (video.duration * 10) + 'px', 'important');
          }
        }
      }
      this._autoplayTimer = setTimeout(() => {
        if (!this._autoplaying) return;
        if (!this.wrapAround && currentIndex === this.items.length - 1) {
          this.stopCustomAutoplay();
        } else {
          this.slider.next();
          this.scheduleNextSlide();
        }
      }, duration);
    };

    if (video) {
      if (video.readyState >= 2 && video.duration) {
        schedule(video.duration * 1000);
      } else {
        const onCanPlay = () => {
          video.removeEventListener("canplay", onCanPlay);
          if (!this._autoplaying) return;
          schedule(video.duration ? video.duration * 1000 : this.speed);
        };
        video.addEventListener("canplay", onCanPlay);
      }
    } else {
      schedule(this.speed);
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(
      new CustomEvent("slider:change", {
        bubbles: true,
        detail: { currentPage: this.selectedIndex },
      })
    );

    if (this.paginationType.includes("pagination-arrow")) {
      this.handleArrowPagination(this.selectedIndex);
    }
    if (this.paginationType.includes("pagination-tab")) {
      this.handleTabsIntoView();
    }

    if (this._autoplaying) {
      this.startCustomAutoplay();
    }
  }

  onReady(selectedElement) {
    this.playSlideMedia(selectedElement);
    if (!theme.config.isTouch) {
      selectedElement.querySelector("animate-element")?.refresh();
    }
    if (this.paginationType.includes("pagination-arrow")) {
      this.handleArrowPagination(0);
    }
    if (this.paginationType.includes("pagination-dot")) {
      this.handleDotPagination();
    }
    if (this.paginationType.includes("pagination-tab")) {
      this.handleTabPagination();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "selected-index" && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      toElement?.querySelector("animate-element")?.refresh();

      if (fromElement?.getAttribute("data-type") === "video") {
        theme.utils.visibleMedia(fromElement.querySelectorAll("video-media"))?.pause();
      }

      if (toElement?.getAttribute("data-type") === "video") {
        const videoMedia = theme.utils.visibleMedia(toElement.querySelectorAll("video-media"));
        if (videoMedia) {
          const videoTag = toElement.querySelector("video");
          if (videoTag) videoTag.currentTime = 0;
          videoMedia.play();
        }
      }
    }
  }

  playSlideMedia(slide) {
    if (slide?.getAttribute("data-type") === "video") {
      const videoMedia = theme.utils.visibleMedia(slide.querySelectorAll("video-media"));
      videoMedia?.play();
    }
  }

  handleArrowPagination(index) {
    // 只查找当前组件下的箭头，避免全局影响

    let parent = this.dataset.sliderId;
    const left = document.querySelector(`#arrow-pagination-${parent} #arrow-pagination-left`);
    const right = document.querySelector(`#arrow-pagination-${parent} #arrow-pagination-right`);

    if (!left || !right) return;

    const last = this.items.length - 1;

    left.style.opacity = index === 0 ? 0 : 1;
    right.style.opacity = index === last ? 0 : 1;
  }
  handleDotPagination() {
    let that = this;
    let parent = this.dataset.sliderId;
    const dots = document.querySelectorAll(`#dot-pagination-${parent} .flickity-page-dot`);
    if (!dots) return;
    dots.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var container = btn.closest('[data-slider-id]');
        if (container) {
          var sliderId = container.getAttribute('data-slider-id');
          var flickityEl = document.querySelector('flickity-play-custom-element[data-slider-id="' + sliderId + '"]');
          if (flickityEl) {
            that.stopCustomAutoplay();
          }
        }
      });
    });
  }
  handleTabPagination() {
    let that = this;
    let parent = this.dataset.sliderId;
    const tabs = document.querySelectorAll(`#tab-pagination-${parent} .flickity-page-tab`);
    if (!tabs) return;
    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var container = btn.closest('[data-slider-id]');
        if (container) {
          var sliderId = container.getAttribute('data-slider-id');
          var flickityEl = document.querySelector('flickity-play-custom-element[data-slider-id="' + sliderId + '"]');
          if (flickityEl) {
            that.stopCustomAutoplay();
          }
        }
      });
    });
  }
  handleTabsIntoView() {
    const currentIndex = this.slider.selectedIndex;
    const currentSlide = this.items[currentIndex];
    let blockId = currentSlide.getAttribute('data-block-id');
    if (blockId) {
      const tab = document.querySelector(`.flickity-page-tab-${blockId}`)
      const container = tab?.closest('.pagination-tab');
      if (!container) return;

      const tabRect = tab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      // if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
      //   tab.scrollIntoView({
      //     behavior: 'smooth',
      //     block: 'nearest',
      //     inline: 'center'
      //   });
      // }
    }
  }

  select(index) {
    this.slider.select(index);
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    this.stopCustomAutoplay();
    // 移除可见性监听
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  }
}

customElements.define("flickity-play-custom-element", FlickityPlayCustomElement);
